/**
 * Minimal `when` clause evaluator (架构方案 §5.9). Contribution points such as
 * menus and keybindings carry an optional `when` expression; the platform
 * evaluates it against a flat context to decide visibility/enablement.
 *
 * Supported grammar (a deliberate subset of VSCode's, no parentheses):
 *   - bare key            -> truthy(ctx[key])
 *   - !key                -> !truthy(ctx[key])
 *   - key == value        -> String(ctx[key]) === value
 *   - key != value        -> String(ctx[key]) !== value
 *   - a && b              -> conjunction (binds tighter than ||)
 *   - a || b              -> disjunction
 * `value` may be single/double quoted. An empty/missing expression is `true`.
 */
export type WhenContext = Record<string, unknown>;

export function evaluateWhen(
  expr: string | undefined,
  ctx: WhenContext,
): boolean {
  if (!expr || expr.trim() === '') {
    return true;
  }
  return expr
    .split('||')
    .some((or) => or.split('&&').every((and) => evaluateAtom(and.trim(), ctx)));
}

function evaluateAtom(atom: string, ctx: WhenContext): boolean {
  if (atom === '') {
    return true;
  }

  const comparison = /^(.+?)(==|!=)(.+)$/.exec(atom);
  if (comparison) {
    const key = comparison[1].trim();
    const op = comparison[2];
    const expected = unquote(comparison[3].trim());
    const actual = stringifyValue(ctx[key]);
    return op === '==' ? actual === expected : actual !== expected;
  }

  if (atom.startsWith('!')) {
    return !isTruthy(ctx[atom.slice(1).trim()]);
  }

  return isTruthy(ctx[atom]);
}

function isTruthy(value: unknown): boolean {
  return (
    value !== undefined && value !== null && value !== false && value !== ''
  );
}

/** Coerce a context value to a comparable string (only meaningful primitives). */
function stringifyValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return '';
}

function unquote(value: string): string {
  if (
    value.length >= 2 &&
    ((value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'")))
  ) {
    return value.slice(1, -1);
  }
  return value;
}
